import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { usePropertiesByBuilding, type Property, type BuildingId } from '@/hooks'
import { Download, ExternalLink, Check, Minus, Home, AlertCircle } from 'lucide-react'

// ============================================================================
// Constants
// ============================================================================

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
}

const DEFAULT_CURRENCY = 'EUR'

// ============================================================================
// Types
// ============================================================================

interface BuildingPropertiesTableProps {
  buildingId: BuildingId
  /** Optional status filter for properties (e.g., 'published' for public view) */
  statusFilter?: 'draft' | 'published' | 'archived'
  /** Show admin actions (edit links). Defaults to true in admin context. */
  showAdminActions?: boolean
  /** Show reserve button for public view */
  showReserveButton?: boolean
  /** Callback when reserve button is clicked */
  onReserve?: (propertyId: string, referenceCode: string) => void
}

/** Configuration for which characteristic columns are visible */
interface VisibleColumns {
  bedrooms: boolean
  bathrooms: boolean
  floorPlan: boolean
  area: boolean
  garage: boolean
  storageRoom: boolean
  terrace: boolean
}

// ============================================================================
// Utility Functions
// ============================================================================

function formatPrice(price: number | undefined, currency: string | undefined): string {
  if (price === undefined || price === null) return '-'
  const symbol = CURRENCY_SYMBOLS[currency || DEFAULT_CURRENCY] || CURRENCY_SYMBOLS[DEFAULT_CURRENCY]
  return `${symbol}${price.toLocaleString('es-ES')}`
}

function formatArea(area: number | undefined): string {
  if (area === undefined || area === null) return '-'
  return `${area.toLocaleString('es-ES')} m²`
}

/**
 * Determine which characteristic columns should be visible based on property data.
 * A column is visible if at least one property has a value for that characteristic.
 */
function getVisibleColumns(properties: Property[]): VisibleColumns {
  const visible: VisibleColumns = {
    bedrooms: false,
    bathrooms: false,
    floorPlan: false,
    area: false,
    garage: false,
    storageRoom: false,
    terrace: false,
  }

  for (const property of properties) {
    // Check bedrooms (top-level or nested in specs)
    const bedrooms = property.bedrooms ?? property.specs?.bedrooms
    if (bedrooms !== undefined && bedrooms !== null) {
      visible.bedrooms = true
    }

    // Check bathrooms (top-level or nested in specs)
    const bathrooms = property.bathrooms ?? property.specs?.bathrooms
    if (bathrooms !== undefined && bathrooms !== null) {
      visible.bathrooms = true
    }

    // Check floor plan
    const floorPlanUrl = (property as any).floor_plan_url as string | null
    if (floorPlanUrl) {
      visible.floorPlan = true
    }

    // Check area (top-level or nested in specs)
    const areaM2 = property.area_m2 ?? property.specs?.area_m2
    if (areaM2 !== undefined && areaM2 !== null) {
      visible.area = true
    }

    // Check garage (characteristics or specs for backward compatibility)
    const hasGarage = property.characteristics?.garage ?? property.specs?.garage
    if (hasGarage !== undefined) {
      visible.garage = true
    }

    // Check storage room
    if (property.characteristics?.storage_room !== undefined) {
      visible.storageRoom = true
    }

    // Check terrace
    if (property.characteristics?.terrace !== undefined) {
      visible.terrace = true
    }
  }

  return visible
}

// ============================================================================
// Sub-Components
// ============================================================================

/** Renders a check mark or dash for boolean fields */
function BooleanCell({ value }: { value: boolean | undefined }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center justify-center text-green-600 dark:text-green-400">
        <Check className="h-4 w-4" aria-label="Sí" />
      </span>
    )
  }
  return (
    <span className="inline-flex items-center justify-center text-muted-foreground">
      <Minus className="h-4 w-4" aria-label="No" />
    </span>
  )
}

/** Status badge for property status */
function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  const styles = {
    draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
  }

  const labels: Record<string, string> = {
    draft: 'Borrador',
    published: 'Publicado',
    archived: 'Archivado',
  }

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  )
}

/** Loading skeleton for the table - shows all possible columns as placeholder */
function TableSkeleton() {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[800px]">
        <thead className="border-b bg-muted/50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">Vivienda</th>
            <th className="px-3 py-3 text-center text-sm font-medium">Dorm.</th>
            <th className="px-3 py-3 text-center text-sm font-medium">Baños</th>
            <th className="px-4 py-3 text-right text-sm font-medium">Precio</th>
            <th className="px-3 py-3 text-center text-sm font-medium">Plano</th>
            <th className="px-4 py-3 text-right text-sm font-medium">Metros</th>
            <th className="px-3 py-3 text-center text-sm font-medium">Garaje</th>
            <th className="px-3 py-3 text-center text-sm font-medium">Trastero</th>
            <th className="px-3 py-3 text-center text-sm font-medium">Terraza</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {[1, 2, 3].map((i) => (
            <tr key={i}>
              <td className="px-4 py-3">
                <div className="h-5 w-24 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-3 py-3 text-center">
                <div className="mx-auto h-5 w-8 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-3 py-3 text-center">
                <div className="mx-auto h-5 w-8 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-4 py-3 text-right">
                <div className="ml-auto h-5 w-20 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-3 py-3 text-center">
                <div className="mx-auto h-5 w-6 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-4 py-3 text-right">
                <div className="ml-auto h-5 w-16 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-3 py-3 text-center">
                <div className="mx-auto h-5 w-6 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-3 py-3 text-center">
                <div className="mx-auto h-5 w-6 animate-pulse rounded bg-muted" />
              </td>
              <td className="px-3 py-3 text-center">
                <div className="mx-auto h-5 w-6 animate-pulse rounded bg-muted" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Empty state when building has no properties */
function EmptyState({ showAddLink, buildingId }: { showAddLink: boolean; buildingId: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 p-8 text-center">
      <Home className="h-10 w-10 text-muted-foreground" />
      <h3 className="mt-3 text-lg font-medium">Sin propiedades</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Este edificio aún no tiene propiedades asignadas.
      </p>
      {showAddLink && (
        <Link
          to={`/properties/create?building=${buildingId}`}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          + Añadir Propiedad
        </Link>
      )}
    </div>
  )
}

/** Error state */
function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
      <AlertCircle className="h-10 w-10 text-destructive" />
      <h3 className="mt-3 text-lg font-medium text-destructive">Error al cargar propiedades</h3>
      <p className="mt-1 text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Intentar de nuevo
        </button>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * BuildingPropertiesTable displays all properties (viviendas) belonging to a building
 * in the Tipologias table format.
 *
 * Columns are dynamically shown based on whether any property has data for that field:
 * - Viviendas (ref code) - always shown
 * - Estado (admin only) - always shown when showAdminActions is true
 * - Dormitorios - only if at least one property has bedrooms
 * - Baños - only if at least one property has bathrooms
 * - Precio - always shown
 * - Plano (download) - only if at least one property has floor_plan_url
 * - Metros - only if at least one property has area_m2
 * - Garaje - only if at least one property has garage defined
 * - Trastero - only if at least one property has storage_room defined
 * - Terraza - only if at least one property has terrace defined
 * - Acciones (admin only) - always shown when showAdminActions is true
 * - Reservar (public only) - always shown when showReserveButton is true
 *
 * Features:
 * - Loading skeleton while fetching
 * - Empty state when no properties exist
 * - Error state with retry option
 * - Responsive horizontal scroll on mobile
 * - Optional reserve button for public view
 * - Optional admin edit links
 */
export function BuildingPropertiesTable({
  buildingId,
  statusFilter,
  showAdminActions = true,
  showReserveButton = false,
  onReserve,
}: BuildingPropertiesTableProps) {
  const { data: properties, isLoading, error, refetch } = usePropertiesByBuilding({
    buildingId,
    status: statusFilter,
  })

  // Calculate which columns should be visible based on property data
  const visibleColumns = useMemo(() => {
    if (!properties || properties.length === 0) {
      return {
        bedrooms: false,
        bathrooms: false,
        floorPlan: false,
        area: false,
        garage: false,
        storageRoom: false,
        terrace: false,
      }
    }
    return getVisibleColumns(properties)
  }, [properties])

  // Loading state
  if (isLoading) {
    return <TableSkeleton />
  }

  // Error state
  if (error) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Ocurrió un error inesperado'}
        onRetry={refetch}
      />
    )
  }

  // Empty state
  if (!properties || properties.length === 0) {
    return <EmptyState showAddLink={showAdminActions} buildingId={buildingId} />
  }

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[600px]">
        <thead className="border-b bg-muted/50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">Vivienda</th>
            {showAdminActions && (
              <th className="px-3 py-3 text-center text-sm font-medium">Estado</th>
            )}
            {visibleColumns.bedrooms && (
              <th className="px-3 py-3 text-center text-sm font-medium">Dorm.</th>
            )}
            {visibleColumns.bathrooms && (
              <th className="px-3 py-3 text-center text-sm font-medium">Baños</th>
            )}
            <th className="px-4 py-3 text-right text-sm font-medium">Precio</th>
            {visibleColumns.floorPlan && (
              <th className="px-3 py-3 text-center text-sm font-medium">Plano</th>
            )}
            {visibleColumns.area && (
              <th className="px-4 py-3 text-right text-sm font-medium">Metros</th>
            )}
            {visibleColumns.garage && (
              <th className="px-3 py-3 text-center text-sm font-medium">Garaje</th>
            )}
            {visibleColumns.storageRoom && (
              <th className="px-3 py-3 text-center text-sm font-medium">Trastero</th>
            )}
            {visibleColumns.terrace && (
              <th className="px-3 py-3 text-center text-sm font-medium">Terraza</th>
            )}
            {showAdminActions && (
              <th className="px-4 py-3 text-right text-sm font-medium">Acciones</th>
            )}
            {showReserveButton && (
              <th className="px-4 py-3 text-center text-sm font-medium">Reservar</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y">
          {properties.map((property: Property) => (
            <PropertyRow
              key={property._id}
              property={property}
              visibleColumns={visibleColumns}
              showAdminActions={showAdminActions}
              showReserveButton={showReserveButton}
              onReserve={onReserve}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================================================
// Property Row Component
// ============================================================================

interface PropertyRowProps {
  property: Property
  visibleColumns: VisibleColumns
  showAdminActions: boolean
  showReserveButton: boolean
  onReserve?: (propertyId: string, referenceCode: string) => void
}

function PropertyRow({ property, visibleColumns, showAdminActions, showReserveButton, onReserve }: PropertyRowProps) {
  // Get bedrooms/bathrooms from either top-level or nested specs
  const bedrooms = property.bedrooms ?? property.specs?.bedrooms
  const bathrooms = property.bathrooms ?? property.specs?.bathrooms
  const areaM2 = property.area_m2 ?? property.specs?.area_m2
  // Check both characteristics and specs for garage (backward compatibility)
  const hasGarage = property.characteristics?.garage ?? property.specs?.garage
  const hasStorageRoom = property.characteristics?.storage_room
  const hasTerrace = property.characteristics?.terrace

  // Display name: use reference_code if available, otherwise property title
  const displayName = property.reference_code || property.title

  // Floor plan document - check if we have a floor plan URL
  // Cast to access floor_plan_url which is added by listByBuilding query
  const floorPlanUrl = (property as any).floor_plan_url as string | null
  const hasFloorPlan = !!floorPlanUrl

  return (
    <tr className="hover:bg-muted/50">
      {/* Vivienda (Property identifier) */}
      <td className="px-4 py-3">
        <div className="font-medium">{displayName}</div>
        {property.reference_code && property.title !== property.reference_code && (
          <div className="text-xs text-muted-foreground truncate max-w-[200px]">{property.title}</div>
        )}
      </td>

      {/* Status (admin only) */}
      {showAdminActions && (
        <td className="px-3 py-3 text-center">
          <StatusBadge status={property.status} />
        </td>
      )}

      {/* Dormitorios */}
      {visibleColumns.bedrooms && (
        <td className="px-3 py-3 text-center text-sm">
          {bedrooms !== undefined ? bedrooms : '-'}
        </td>
      )}

      {/* Baños */}
      {visibleColumns.bathrooms && (
        <td className="px-3 py-3 text-center text-sm">
          {bathrooms !== undefined ? bathrooms : '-'}
        </td>
      )}

      {/* Precio */}
      <td className="px-4 py-3 text-right text-sm font-medium">
        {formatPrice(property.price, property.price_currency)}
      </td>

      {/* Plano (Floor plan download) */}
      {visibleColumns.floorPlan && (
        <td className="px-3 py-3 text-center">
          {hasFloorPlan && floorPlanUrl ? (
            <a
              href={floorPlanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded p-1 text-primary hover:bg-primary/10"
              title="Descargar plano"
            >
              <Download className="h-4 w-4" />
            </a>
          ) : (
            <span className="text-muted-foreground">
              <Minus className="h-4 w-4 mx-auto" />
            </span>
          )}
        </td>
      )}

      {/* Metros (Area) */}
      {visibleColumns.area && (
        <td className="px-4 py-3 text-right text-sm">
          {formatArea(areaM2)}
        </td>
      )}

      {/* Garaje */}
      {visibleColumns.garage && (
        <td className="px-3 py-3 text-center">
          <BooleanCell value={hasGarage} />
        </td>
      )}

      {/* Trastero (Storage room) */}
      {visibleColumns.storageRoom && (
        <td className="px-3 py-3 text-center">
          <BooleanCell value={hasStorageRoom} />
        </td>
      )}

      {/* Terraza */}
      {visibleColumns.terrace && (
        <td className="px-3 py-3 text-center">
          <BooleanCell value={hasTerrace} />
        </td>
      )}

      {/* Admin actions */}
      {showAdminActions && (
        <td className="px-4 py-3 text-right">
          <Link
            to={`/properties/${property._id}/edit`}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-primary hover:bg-muted"
          >
            <ExternalLink className="h-3 w-3" />
            Editar
          </Link>
        </td>
      )}

      {/* Reserve button (public view) */}
      {showReserveButton && (
        <td className="px-4 py-3 text-center">
          <button
            type="button"
            onClick={() => onReserve?.(property._id, displayName)}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            Reservar
          </button>
        </td>
      )}
    </tr>
  )
}

export default BuildingPropertiesTable
